// UserHeader.tsx

import React, { useEffect, useRef, useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";

import Avatar from "../components/Avatar";
import GrowniverseLogo from "../Photos/Logo/growniverse-logo-full-bordered.svg";
import GrowniverseLogoFull from "../Photos/Logo/growniverse-logo-full.svg";

import "../components/WelcomeSection.css";
import "./Design/Dashboard.css";

const UserHeader: React.FC = () => {
  const navigate = useNavigate();
  const mounted = useRef(false);

  const theme = useTheme();

  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(
    theme.breakpoints.between("sm", "md")
  );

  const [userName, setUserName] = useState("");
  const [profileImage, setProfileImage] =
    useState<string | null>(null);

  const [anchorEl, setAnchorEl] =
    useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  // ============================================================
  // LOAD USER
  // ============================================================

  useEffect(() => {
    if (mounted.current) return;

    const appKey =
      localStorage.getItem("appkey");

    if (
      !appKey ||
      appKey === "NeedVerification"
    ) {
      window.location.href = "/";
      return;
    }

    try {
      const storedProfile =
        localStorage.getItem(
          "userProfile"
        );

      if (storedProfile) {
        const userProfile =
          JSON.parse(storedProfile);

        setUserName(
          userProfile.fullName ?? ""
        );

        setProfileImage(
          userProfile.profileImage ?? null
        );
      }
    } catch (error) {
      console.warn(
        "Invalid userProfile in localStorage",
        error
      );
    }

    mounted.current = true;
  }, []);

  // ============================================================
  // MENU
  // ============================================================

  const handleAvatarClick = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const goTo = (path: string) => {
    handleClose();
    navigate(path);
  };

  // ============================================================
  // LOGOUT
  // ============================================================

  const handleLogout = () => {
    handleClose();

    localStorage.clear();

    navigate("/");
  };

  // ============================================================
  // RESPONSIVE SIZES
  // ============================================================

  const avatarOuterSize = isXs
    ? 48
    : isSm
    ? 58
    : 72;

  const avatarInnerSize = isXs
    ? 36
    : isSm
    ? 46
    : 58;

  const logoSize = isXs
    ? 64
    : isSm
    ? 76
    : 84;

  const centerLogoWidth = isXs
    ? 205
    : isSm
    ? 310
    : 420;

  // ============================================================
  // PROFILE IMAGE
  // ============================================================

  const hasProfileImage =
    Boolean(profileImage);

  return (
    <>
      {/* ========================================================
          FUTURISTIC HEADER
      ======================================================== */}

      <header
        style={{
          position: "relative",
          zIndex: 1200,
          width: "100%",
          minHeight: isXs ? 68 : 88,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          background:
            "linear-gradient(135deg, rgba(5,8,16,0.98), rgba(8,12,24,0.96))",

          borderBottom:
            "1px solid rgba(0,246,255,0.16)",

          boxShadow:
            "0 8px 35px rgba(0,0,0,0.45), inset 0 -1px 0 rgba(255,255,255,0.025)",

          overflow: "hidden",
        }}
      >
        {/* ======================================================
            BACKGROUND GRID
        ====================================================== */}

        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            opacity: 0.10,

            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",

            backgroundSize: "32px 32px",
          }}
        />

        {/* ======================================================
            CYAN GLOW
        ====================================================== */}

        <div
          style={{
            position: "absolute",
            width: 240,
            height: 240,
            left: -100,
            top: -170,
            borderRadius: "50%",
            background:
              "rgba(0,246,255,0.09)",
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />

        {/* ======================================================
            PURPLE GLOW
        ====================================================== */}

        <div
          style={{
            position: "absolute",
            width: 260,
            height: 260,
            right: -120,
            top: -170,
            borderRadius: "50%",
            background:
              "rgba(139,92,255,0.10)",
            filter: "blur(85px)",
            pointerEvents: "none",
          }}
        />

        {/* ======================================================
            LEFT LOGO
        ====================================================== */}

        <div
          style={{
            position: "absolute",
            left: isXs ? 10 : 20,
            top: "50%",
            transform:
              "translateY(-50%)",

            display: "flex",
            alignItems: "center",

            zIndex: 3,
          }}
        >
          <Link
            to="/"
            aria-label="Growniverse home"
            style={{
              display: "inline-flex",
              alignItems: "center",
              textDecoration: "none",
            }}
          >
            <div
              style={{
                position: "relative",
                padding: "5px",

                border:
                  "1px solid rgba(0,246,255,0.16)",

                background:
                  "rgba(0,246,255,0.025)",

                boxShadow:
                  "0 0 20px rgba(0,246,255,0.05)",
              }}
            >
              <img
                src={GrowniverseLogo}
                width={logoSize}
                height="auto"
                alt="Growniverse"
                style={{
                  display: "block",
                  filter:
                    "drop-shadow(0 0 12px rgba(0,246,255,0.20))",
                }}
              />
            </div>
          </Link>
        </div>

        {/* ======================================================
            CENTER LOGO
        ====================================================== */}

        <div
          style={{
            position: "relative",
            zIndex: 2,

            width: "100%",
            maxWidth: centerLogoWidth,

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            paddingLeft: isXs
              ? 70
              : 100,

            paddingRight: isXs
              ? 70
              : 100,
          }}
        >
          <img
            src={GrowniverseLogoFull}
            alt="Growniverse"
            style={{
              width: "100%",
              maxWidth:
                centerLogoWidth,

              height: "auto",

              display: "block",

              transform:
                "translateY(-1px)",

              filter:
                "drop-shadow(0 8px 25px rgba(0,0,0,0.55)) drop-shadow(0 0 10px rgba(0,246,255,0.08))",
            }}
          />
        </div>

        {/* ======================================================
            RIGHT USER ACCOUNT
        ====================================================== */}

        <div
          style={{
            position: "absolute",
            right: isXs ? 10 : 20,
            top: "50%",
            transform:
              "translateY(-50%)",

            display: "flex",
            alignItems: "center",

            zIndex: 5,
          }}
        >
          <Tooltip
            title={
              userName
                ? userName
                : "Account"
            }
            arrow
          >
            <IconButton
              onClick={
                handleAvatarClick
              }
              aria-label="open account menu"
              sx={{
                width:
                  avatarOuterSize,
                height:
                  avatarOuterSize,

                padding: 0,

                borderRadius:
                  "50%",

                background:
                  "transparent",

                position:
                  "relative",

                "&:hover": {
                  background:
                    "rgba(0,246,255,0.05)",
                },
              }}
            >
              {/* ==================================================
                  OUTER NEON RING
              ================================================== */}

              <div
                style={{
                  width:
                    avatarOuterSize,
                  height:
                    avatarOuterSize,

                  borderRadius:
                    "50%",

                  padding: 2,

                  display:
                    "flex",

                  justifyContent:
                    "center",

                  alignItems:
                    "center",

                  background:
                    "conic-gradient(from 0deg, #00f6ff, #8b5cff, #00f6ff)",

                  boxShadow:
                    "0 0 20px rgba(0,246,255,0.18)",

                  position:
                    "relative",
                }}
              >
                {/* ==================================================
                    INNER AVATAR
                ================================================== */}

                <div
                  style={{
                    width:
                      avatarInnerSize,
                    height:
                      avatarInnerSize,

                    borderRadius:
                      "50%",

                    overflow:
                      "hidden",

                    display:
                      "flex",

                    alignItems:
                      "center",

                    justifyContent:
                      "center",

                    background:
                      "#0a0e18",

                    border:
                      "2px solid #080b13",

                    position:
                      "relative",
                  }}
                >
                  {hasProfileImage ? (
                    <img
                      src={`data:image/jpeg;base64,${profileImage}`}
                      alt="Profile"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit:
                          "cover",
                        display:
                          "block",
                      }}
                      onError={(event) => {
                        event.currentTarget.style.display =
                          "none";
                      }}
                    />
                  ) : (
                    <Avatar
                      name={userName}
                      size={
                        avatarInnerSize -
                        8
                      }
                    />
                  )}
                </div>

                {/* ==================================================
                    ONLINE INDICATOR
                ================================================== */}

                <span
                  style={{
                    position:
                      "absolute",

                    right:
                      isXs
                        ? 2
                        : 4,

                    bottom:
                      isXs
                        ? 2
                        : 4,

                    width:
                      isXs
                        ? 8
                        : 10,

                    height:
                      isXs
                        ? 8
                        : 10,

                    borderRadius:
                      "50%",

                    background:
                      "#00f6ff",

                    border:
                      "2px solid #080b13",

                    boxShadow:
                      "0 0 10px #00f6ff",
                  }}
                />
              </div>
            </IconButton>
          </Tooltip>
        </div>

        {/* ======================================================
            BOTTOM SCAN LINE
        ====================================================== */}

        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: "18%",
            right: "18%",
            height: 1,

            background:
              "linear-gradient(90deg, transparent, rgba(0,246,255,0.45), transparent)",

            pointerEvents:
              "none",
          }}
        />
      </header>

      {/* ========================================================
          ACCOUNT MENU
      ======================================================== */}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        MenuListProps={{
          disablePadding: true,
        }}
        PaperProps={{
          sx: {
            mt: 1.5,

            minWidth:
              isXs ? 220 : 250,

            overflow:
              "hidden",

            bgcolor:
              "rgba(5,9,18,0.98)",

            color:
              "#ffffff",

            border:
              "1px solid rgba(0,246,255,0.20)",

            borderRadius:
              "12px",

            boxShadow:
              "0 20px 60px rgba(0,0,0,0.65), 0 0 30px rgba(0,246,255,0.06)",

            backdropFilter:
              "blur(20px)",

            "& .MuiMenuItem-root": {
              color:
                "#e8eef7",

              fontSize:
                "13px",

              fontWeight:
                600,

              padding:
                "13px 18px",

              borderBottom:
                "1px solid rgba(255,255,255,0.055)",

              transition:
                "all 0.2s ease",

              "&:hover": {
                background:
                  "rgba(0,246,255,0.08)",

                color:
                  "#00f6ff",
              },
            },

            "& .MuiMenuItem-root:last-child": {
              borderBottom:
                "none",
            },
          },
        }}
      >
        {/* ======================================================
            MENU HEADER
        ====================================================== */}

        <div
          style={{
            padding:
              "17px 18px",

            borderBottom:
              "1px solid rgba(255,255,255,0.08)",

            background:
              "linear-gradient(135deg, rgba(0,246,255,0.07), rgba(139,92,255,0.05))",
          }}
        >
          <div
            style={{
              display:
                "flex",

              alignItems:
                "center",

              gap:
                "12px",
            }}
          >
            {/* Small avatar */}

            <div
              style={{
                width:
                  "42px",

                height:
                  "42px",

                minWidth:
                  "42px",

                padding:
                  "2px",

                borderRadius:
                  "50%",

                background:
                  "conic-gradient(#00f6ff, #8b5cff, #00f6ff)",
              }}
            >
              <div
                style={{
                  width:
                    "100%",

                  height:
                    "100%",

                  borderRadius:
                    "50%",

                  overflow:
                    "hidden",

                  background:
                    "#080c15",

                  display:
                    "flex",

                  alignItems:
                    "center",

                  justifyContent:
                    "center",
                }}
              >
                {hasProfileImage ? (
                  <img
                    src={`data:image/jpeg;base64,${profileImage}`}
                    alt="Profile"
                    style={{
                      width:
                        "100%",
                      height:
                        "100%",
                      objectFit:
                        "cover",
                    }}
                  />
                ) : (
                  <Avatar
                    name={userName}
                    size={34}
                  />
                )}
              </div>
            </div>

            <div
              style={{
                minWidth:
                  0,
              }}
            >
              <div
                style={{
                  color:
                    "#ffffff",

                  fontSize:
                    "14px",

                  fontWeight:
                    700,

                  overflow:
                    "hidden",

                  textOverflow:
                    "ellipsis",

                  whiteSpace:
                    "nowrap",

                  maxWidth:
                    "150px",
                }}
              >
                {userName ||
                  "User"}
              </div>

              <div
                style={{
                  marginTop:
                    "4px",

                  display:
                    "flex",

                  alignItems:
                    "center",

                  gap:
                    "6px",

                  color:
                    "#8d9aaa",

                  fontSize:
                    "9px",

                  fontWeight:
                    700,

                  letterSpacing:
                    "1.5px",
                }}
              >
                <span
                  style={{
                    width:
                      "6px",

                    height:
                      "6px",

                    borderRadius:
                      "50%",

                    background:
                      "#00f6ff",

                    boxShadow:
                      "0 0 8px #00f6ff",
                  }}
                />

                ONLINE
              </div>
            </div>
          </div>
        </div>

        {/* ======================================================
            PROFILE
        ====================================================== */}

        <MenuItem
          onClick={() =>
            goTo("/profile")
          }
        >
          <span
            style={{
              width:
                "28px",

              color:
                "#00f6ff",

              fontSize:
                "17px",

              marginRight:
                "10px",
            }}
          >
            ◎
          </span>

          Profile
        </MenuItem>

        {/* ======================================================
            SUPPORT
        ====================================================== */}

        <MenuItem
          onClick={() =>
            goTo("/support")
          }
        >
          <span
            style={{
              width:
                "28px",

              color:
                "#8b7cff",

              fontSize:
                "17px",

              marginRight:
                "10px",
            }}
          >
            ?
          </span>

          Support
        </MenuItem>

        {/* ======================================================
            LOGOUT
        ====================================================== */}

        <MenuItem
          onClick={
            handleLogout
          }
          sx={{
            color:
              "#ff8e9b !important",

            "&:hover": {
              background:
                "rgba(255,80,100,0.08) !important",

              color:
                "#ff6678 !important",
            },
          }}
        >
          <span
            style={{
              width:
                "28px",

              color:
                "#ff7182",

              fontSize:
                "17px",

              marginRight:
                "10px",
            }}
          >
            ↪
          </span>

          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserHeader;